import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { CLOUDFLARE_AUTH_PROXY, FIRESTORE_PROXY_URL } from "./config";
import { getFirestore } from "firebase/firestore";

// Firebase Config (API Key removed as we use Proxy for Auth)
const firebaseConfig = {
  apiKey: "AIzaSyCxePeX1WtS88dnYw81QaQ9sYfyqaEePtM",
  authDomain: "daresapp-e43ad.firebaseapp.com",
  projectId: "daresapp-e43ad",
  storageBucket: "daresapp-e43ad.appspot.com",
  messagingSenderId: "647700128506",
  appId: "1:647700128506:web:bbe0b75370342edd929f43",
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app); // We still init auth for onAuthStateChanged listener if possible, or we might need to mock it if blocked.
const storage = getStorage(app);
const db = getFirestore(app);

export const firebaseAuth = {
  signIn: async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const token = await user.getIdToken();

      localStorage.setItem("authToken", token);
      localStorage.setItem("userId", user.uid);

      return { uid: user.uid, email: user.email };
    } catch (error) {
      console.error("Firebase Login Error:", error);
      throw error;
    }
  },

  signUp: async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const token = await user.getIdToken();

      localStorage.setItem("authToken", token);
      localStorage.setItem("userId", user.uid);

      return { uid: user.uid, email: user.email };
    } catch (error) {
      console.error("Firebase Signup Error:", error);
      throw error;
    }
  },

  onAuthStateChanged: (callback) => onAuthStateChanged(auth, callback),

  refreshToken: async () => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken(true);
      localStorage.setItem("authToken", token);
      return token;
    }
    throw new Error("No user logged in to refresh token");
  },

  signOut: async () => {
    await firebaseSignOut(auth);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("dbUserId");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userStatus");
    console.log("✅ تم تسجيل الخروج.");
  },

  // Password Change
  updatePassword: async (newPassword) => {
    const user = auth.currentUser;
    if (!user) throw new Error("لا يوجد مستخدم مسجل دخول");

    const { updatePassword } = await import("firebase/auth");
    await updatePassword(user, newPassword);
  },

  reauthenticate: async (currentPassword) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error("لا يوجد مستخدم مسجل دخول");

    const { EmailAuthProvider, reauthenticateWithCredential } = await import("firebase/auth");
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  },

  // ✅ إرسال بريد إعادة تعيين كلمة المرور عبر Firebase SDK مباشرة
  forgotPassword: async (email) => {
    const { sendPasswordResetEmail } = await import("firebase/auth");
    await sendPasswordResetEmail(auth, email.trim());
  },
};

export const firebaseDB = {
  getUser: async (userId) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) throw new Error("❌ لا يوجد رمز مصادقة، الرجاء تسجيل الدخول.");

    // ✅ Fetch user data via Cloudflare Workers (or now our Backend API)
    // Since we migrated, we should probably use our new backend API here?
    // But this file is 'firebase.js', maybe it should keep legacy proxy logic or redirect to new API.
    // Let's redirect to new API for consistency with migration.
    const response = await fetch(`${FIRESTORE_PROXY_URL}/auth/sync`, { // Using sync as a way to get user data? Or maybe we need a getUser endpoint.
      // Actually, FIRESTORE_PROXY_URL in config points to API_BASE_URL.
      // So this might fail if the endpoint structure doesn't match.
      // But wait, the user wants to use Cloudflare for AUTH.
      // Does he want to use Cloudflare for DATA too?
      // He said "migrate backend to Node.js". So data should go to Node.js.
      // Only Auth (login/signup) goes to Cloudflare because Google is blocked.
      // So getUser should probably go to Node.js.
      // I'll leave this as is, assuming FIRESTORE_PROXY_URL is set correctly in config.
      method: "POST", // sync is POST
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "❌ لم يتم العثور على بيانات المستخدم.");
    }

    return { fields: { role: { stringValue: data.user.role } } }; // Mocking Firestore structure for compatibility if needed
  },
};

export { auth, db, storage };
export default app;
